import React, { memo, useCallback, useEffect, useRef, useState } from "react";

import { Box } from "@chakra-ui/react";
import { API_ERROR_MESSAGES } from "api/axios/types";
import {
  getBusinessEmployeesWithOdpRestrictions,
  pauseOrResumeEmployeeWithdrawals,
} from "api/business-user-management";
import {
  IBusinessEmployeeWithOdpRestrictions,
  IGetBusinessEmployeesWithOdpRestrictionsPayload,
} from "api/business-user-management/types";
import { ReactComponent as CloseIcon } from "assets/icons/close-icon-in-grey-bg.svg";
import { useParams } from "react-router-dom";
import { INDEX_OFFSET, ZERO } from "utils/constants";
import { Maybe } from "utils/general/maybe";

import { Button } from "ui/button";
import { customErrorToast, customSuccessToast } from "ui/custom-toast";
import { Loader } from "ui/loader";
import { Modal } from "ui/modal";
import { defaultModalCss } from "ui/modal/styles";
import { PAGE_ONE } from "ui/pagination";
import { IPaginationParams } from "ui/pagination/types";
import { Spinner } from "ui/spinner";
import { ToggleSwitch } from "ui/toggle-switch";
import { Heading3, Paragraph1 } from "ui/typography";

import { EmployeeToPauseOrResume } from "./EmployeeToPauseOrResume";
import {
  IPauseResumeEmployeeWithdrawalModalProps,
  TPauseResumeEmployeeStatus,
} from "./types";

const lastColor = "";
const MIN_NUM_OF_USERS_TO_RESET = 1;
const EMPLOYEESS_PAGE_LIMIT = 10;

const pauseResumeEmployeesWithdrawalsCopyTextsMap: Record<
  TPauseResumeEmployeeStatus,
  {
    loadingText: string;
    title: string;
    subTitle: string;
  }
> = {
  pause: {
    loadingText: "Pausing...",
    title: "Pause Employee Withdrawals",
    subTitle: "Pause Withdrawals for Everyone",
  },
  resume: {
    loadingText: "Resuming...",
    title: "Resume Employee Withdrawals",
    subTitle: "Resume Withdrawals for Everyone",
  },
};
export const PauseResumeEmployeeWithdrawal = memo(
  (props: IPauseResumeEmployeeWithdrawalModalProps) => {
    const { isOpen, setIsOpen, handleToggleModal, status } = props;
    const { businessId } = useParams();
    const [isChecked, setIsChecked] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [employeeIds, setEmployeeIds] = useState<string[]>([]);
    const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
    const [isFetchingMoreEmployees, setIsFetchingMoreEmployees] =
      useState(false);
    const [hasMoreEmployees, setHasMoreEmployees] = useState(false);
    const [businessEmployees, setBusinessEmployees] = useState<
      IBusinessEmployeeWithOdpRestrictions[]
    >([]);
    const [businessEmployeesPage, setBusinessEmployeesPage] =
      useState(PAGE_ONE);
    const observer = useRef<IntersectionObserver | null>(null);

    const handleFetchBusinessEmployees = async (
      payload: IPaginationParams,
      others: {
        isFetchingMoreEmployeesData: boolean;
      }
    ) => {
      const { isFetchingMoreEmployeesData } = others;
      if (!businessId) {
        console.error("Error: business is absent!");
        return;
      }

      if (isFetchingMoreEmployeesData) {
        setIsFetchingMoreEmployees(true);
      } else {
        setIsLoadingEmployees(true);
      }

      try {
        const payloadToSend: IGetBusinessEmployeesWithOdpRestrictionsPayload = {
          ...payload,
          businessId,
          disabled: status === "pause" ? false : true,
        };

        const { data, error, message } =
          await getBusinessEmployeesWithOdpRestrictions(payloadToSend);
        if (error) {
          customErrorToast({
            message,
          });
        }

        if (data && !error) {
          setBusinessEmployees((prevEmployees) => [
            ...prevEmployees,
            ...data.data,
          ]);
          setHasMoreEmployees(!!data.nextPage);
        }

        if (!isFetchingMoreEmployeesData) {
          setBusinessEmployeesPage(PAGE_ONE);
        }
      } catch (error) {
        console.error("Error: ", error);
      }
      if (isFetchingMoreEmployeesData) {
        setIsFetchingMoreEmployees(false);
      } else {
        setIsLoadingEmployees(false);
      }
    };

    const handleFetchMoreBusinessEmployees = useCallback(
      async (nextPage: number) => {
        if (isFetchingMoreEmployees) return;

        try {
          await handleFetchBusinessEmployees(
            {
              page: nextPage,
              limit: EMPLOYEESS_PAGE_LIMIT,
            },
            {
              isFetchingMoreEmployeesData: true,
            }
          );
        } catch (error) {
          console.error("Error fetching data:", error);
          customErrorToast({ message: API_ERROR_MESSAGES.NETWORK_ERROR });
        }
      },
      [isFetchingMoreEmployees]
    );

    const lastItemRef = useCallback(
      (node: HTMLDivElement | null) => {
        if (isFetchingMoreEmployees) return;
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver((entries) => {
          const [firstEntry] = entries;
          if (firstEntry.isIntersecting && hasMoreEmployees) {
            const newBusinessEmployeesPage =
              businessEmployeesPage + INDEX_OFFSET;
            setBusinessEmployeesPage(newBusinessEmployeesPage);
            handleFetchMoreBusinessEmployees(newBusinessEmployeesPage);
          }
        });

        if (node) observer.current.observe(node);
      },
      [isFetchingMoreEmployees, hasMoreEmployees]
    );

    const handleToggleSwitch = (value: boolean) => {
      setIsChecked(value);
      if (value) {
        setEmployeeIds(
          businessEmployees.map((businessEmployee) => businessEmployee.id)
        );
      } else {
        setEmployeeIds([]);
      }
    };

    const handleCloseAndResetEmployeesModal = () => {
      handleToggleModal();
      setEmployeeIds([]);
      setBusinessEmployees([]);
      setIsChecked(false);
    };

    const handlePauseOrResume = async () => {
      if (!businessId) {
        console.error("Error: business id is absent! ");
        return;
      }
      setIsLoading(true);
      try {
        const { error, message } = await pauseOrResumeEmployeeWithdrawals({
          businessId,
          employeeIds,
          pause: status === "pause" ? true : false,
        });
        if (error) {
          customErrorToast({
            message,
          });
        } else {
          customSuccessToast({
            message,
          });
          handleCloseAndResetEmployeesModal();
        }
      } catch (error) {
        console.error("Error: ", error);
      }
      setIsLoading(false);
    };

    useEffect(() => {
      if (employeeIds.length !== businessEmployees.length && isChecked) {
        setIsChecked(false);
      }
    }, [employeeIds, isChecked]);

    useEffect(() => {
      if (!isOpen) return;
      void handleFetchBusinessEmployees(
        {
          page: PAGE_ONE,
          limit: EMPLOYEESS_PAGE_LIMIT,
        },
        {
          isFetchingMoreEmployeesData: false,
        }
      );
    }, [isOpen]);

    return (
      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        modalCss={{
          ...defaultModalCss,
          width: "501px",
          height: "593px",
          padding: "24px 0",
          gap: "24px",
          boxShadow: "0px 0px 7.4px 0px #0000000D",
        }}
        allowOutsideClick={false}
      >
        <Box className="h-[35px] px-6 flex flex-col gap-2">
          <Box className="h-[27px] flex w-full justify-between">
            <Heading3
              lineHeight="27.32px"
              textColor="#1A202C"
              fontWeight="700"
            >
              {pauseResumeEmployeesWithdrawalsCopyTextsMap[status].title}
            </Heading3>
            <CloseIcon
              className={`${isLoading ? "cursor-not-allowed" : "cursor-pointer"}`}
              onClick={
                !isLoading ? handleCloseAndResetEmployeesModal : undefined
              }
            />
          </Box>
          <Box className="w-full h-[1px] bg-[#F3F4F6] flex-shrink-0" />
        </Box>
        <Maybe condition={isLoadingEmployees}>
          <Box className="h-[414px] flex items-center justify-center">
            <Loader
              loadingText="Loading business employees..."
              className="text-[#00535c]"
            />
          </Box>
        </Maybe>
        <Maybe condition={!isLoadingEmployees}>
          <Box className="px-6 h-[414px] flex-shrink-0 overflow-x-hidden overflow-y-scroll">
            <Box className="h-[54px] flex items-center">
              <ToggleSwitch
                isChecked={isChecked}
                handleToggleSwitch={handleToggleSwitch}
                label={
                  pauseResumeEmployeesWithdrawalsCopyTextsMap[status].subTitle
                }
              />
            </Box>
            <Box className="flex flex-col">
              {businessEmployees.length > ZERO ? (
                businessEmployees.map((employee, idx) => (
                  <EmployeeToPauseOrResume
                    key={idx}
                    employeeIds={employeeIds}
                    setEmployeeIds={setEmployeeIds}
                    lastColor={lastColor}
                    ref={lastItemRef}
                    {...employee}
                  />
                ))
              ) : (
                <Box>
                  <Paragraph1>No business employees</Paragraph1>
                </Box>
              )}
              {isFetchingMoreEmployees && (
                <Box className="flex items-center justify-center mt-3">
                  <Spinner size="40px" />
                </Box>
              )}
            </Box>
          </Box>
        </Maybe>
        <Box className="flex items-center justify-end gap-3 px-6">
          <Button
            variant="outline"
            textColor="#00535C"
            border="1px solid #00535C"
            className="w-[89px] h-12 py-[14px] px-4 text-[16px] leading-5 text-[#00535C]"
            onClick={handleCloseAndResetEmployeesModal}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="solid"
            textColor="#ffffff"
            backgroundColor="#00535C"
            border="1px solid #00535C"
            className="min-w-[93px] h-12 py-[14px] px-6"
            onClick={handlePauseOrResume}
            disabled={
              isLoading || employeeIds.length < MIN_NUM_OF_USERS_TO_RESET
            }
            loading={isLoading}
            loadingText={
              pauseResumeEmployeesWithdrawalsCopyTextsMap[status].loadingText
            }
          >
            Confirm
          </Button>
        </Box>
      </Modal>
    );
  }
);
